import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { User, Key, Bell, Trash, Gauge, UsersThree, Plus, PaperPlaneTilt, X, Crown, PencilSimple, Check } from '@phosphor-icons/react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

export default function Settings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [notifSettings, setNotifSettings] = useState(null);
  const [notifLoading, setNotifLoading] = useState(true);
  const [testingNotif, setTestingNotif] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  // Quota state
  const [quotas, setQuotas] = useState(null);
  const [editingQuotas, setEditingQuotas] = useState(false);
  const [quotaForm, setQuotaForm] = useState({ hourly_limit: 100, daily_limit: 1000 });
  // Workspace state
  const [workspaces, setWorkspaces] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [showCreateWs, setShowCreateWs] = useState(false);
  const [wsName, setWsName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [invitingWs, setInvitingWs] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API}/stats`, { credentials: 'include' });
        if (response.ok) setStats(await response.json());
      } catch (error) { console.error('Failed to fetch stats'); }
    };
    const fetchNotifSettings = async () => {
      try {
        const response = await fetch(`${API}/notifications/settings`, { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          setNotifSettings(data);
        }
      } catch (error) { console.error('Failed to fetch notification settings'); }
      finally { setNotifLoading(false); }
    };
    fetchStats();
    fetchNotifSettings();
    // Fetch quotas
    fetch(`${API}/settings/quotas`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) { setQuotas(d); setQuotaForm({ hourly_limit: d.hourly_limit, daily_limit: d.daily_limit }); } })
      .catch(() => {});
    // Fetch workspaces
    fetch(`${API}/workspaces`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(setWorkspaces)
      .catch(() => {});
    // Fetch pending invites
    fetch(`${API}/invites/pending`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(setPendingInvites)
      .catch(() => {});
  }, []);

  const updateNotifSettings = async (updates) => {
    try {
      const response = await fetch(`${API}/notifications/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates)
      });
      if (response.ok) {
        const data = await response.json();
        setNotifSettings(data);
        toast.success('Notification settings updated');
      }
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const addEmailRecipient = () => {
    if (!emailInput || !emailInput.includes('@')) return;
    const current = notifSettings?.email_recipients || [];
    if (current.includes(emailInput)) { setEmailInput(''); return; }
    updateNotifSettings({ email_recipients: [...current, emailInput] });
    setEmailInput('');
  };

  const removeEmailRecipient = (email) => {
    const current = notifSettings?.email_recipients || [];
    updateNotifSettings({ email_recipients: current.filter(e => e !== email) });
  };

  const testNotification = async () => {
    setTestingNotif(true);
    try {
      const response = await fetch(`${API}/notifications/test`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        const results = data.results;
        if (results.slack || results.email) {
          toast.success(`Test sent — Slack: ${results.slack ? 'OK' : 'skipped'}, Email: ${results.email ? 'OK' : 'skipped'}`);
        } else {
          toast.info('No channels are configured yet');
        }
      }
    } catch (error) {
      toast.error('Test notification failed');
    } finally {
      setTestingNotif(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div><h1 className="font-heading font-bold text-2xl">Settings</h1><p className="text-kova-slate text-sm mt-1">Manage your account and preferences</p></div>

      <div className="card-brutal p-6 space-y-4">
        <h2 className="font-heading font-bold text-lg flex items-center gap-2"><User size={20} className="text-kova-cyan" />Profile</h2>
        <div className="flex items-center gap-4">
          {user?.picture ? <img src={user.picture} alt={user.name} className="w-16 h-16 object-cover" /> : <div className="w-16 h-16 bg-kova-card2 flex items-center justify-center text-2xl font-mono">{user?.name?.charAt(0)?.toUpperCase()}</div>}
          <div><p className="font-medium text-lg">{user?.name}</p><p className="text-kova-slate text-sm">{user?.email}</p></div>
        </div>
      </div>

      <div className="card-brutal p-6 space-y-4">
        <h2 className="font-heading font-bold text-lg flex items-center gap-2"><Key size={20} className="text-kova-cyan" />API Keys</h2>
        <p className="text-kova-slate text-sm">Your API keys are securely stored with your connections. Manage them in the Connections page.</p>
        <Button variant="outline" onClick={() => navigate('/connections')} className="border-kova-cyan/12 hover:bg-kova-card2 rounded-none">Manage Connections</Button>
      </div>

      {/* Notification Settings (F-002) */}
      <div className="card-brutal p-6 space-y-5" data-testid="notification-settings">
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-bold text-lg flex items-center gap-2">
            <Bell size={20} className="text-kova-cyan" />Push Notifications
          </h2>
          <Button
            variant="outline"
            size="sm"
            onClick={testNotification}
            disabled={testingNotif}
            className="border-kova-cyan/30 text-kova-cyan hover:bg-kova-cyan/10 rounded-none text-xs font-mono"
            data-testid="test-notification-btn"
          >
            {testingNotif ? 'Sending...' : 'Test Notification'}
          </Button>
        </div>

        {notifLoading ? (
          <p className="text-kova-slate text-sm font-mono">Loading settings...</p>
        ) : (
          <>
            {/* Slack Configuration */}
            <div className="p-4 bg-kova-bg border border-kova-cyan/12 space-y-3" data-testid="slack-config">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Slack Notifications</Label>
                  <p className="text-[10px] text-kova-slate mt-0.5">Block Kit formatted with severity color bars</p>
                </div>
                <Switch
                  checked={notifSettings?.slack_enabled || false}
                  onCheckedChange={(checked) => updateNotifSettings({ slack_enabled: checked })}
                  data-testid="slack-enabled-toggle"
                />
              </div>
              {notifSettings?.slack_enabled && (
                <div className="space-y-2 pt-2 border-t border-kova-cyan/12">
                  <Label className="text-xs text-kova-slate">Webhook URL</Label>
                  <input
                    type="url"
                    placeholder="https://hooks.slack.com/services/..."
                    value={notifSettings?.slack_webhook_url || ''}
                    onChange={(e) => setNotifSettings(prev => ({ ...prev, slack_webhook_url: e.target.value }))}
                    onBlur={(e) => { if (e.target.value) updateNotifSettings({ slack_webhook_url: e.target.value }); }}
                    className="w-full bg-kova-card border border-kova-cyan/12 text-white font-mono text-xs p-2 outline-none focus:border-kova-cyan/50"
                    data-testid="slack-webhook-input"
                  />
                  <Label className="text-xs text-kova-slate">Channel Name (display only)</Label>
                  <input
                    type="text"
                    placeholder="#kova-alerts"
                    value={notifSettings?.slack_channel_name || ''}
                    onChange={(e) => setNotifSettings(prev => ({ ...prev, slack_channel_name: e.target.value }))}
                    onBlur={(e) => updateNotifSettings({ slack_channel_name: e.target.value })}
                    className="w-full bg-kova-card border border-kova-cyan/12 text-white font-mono text-xs p-2 outline-none focus:border-kova-cyan/50"
                    data-testid="slack-channel-input"
                  />
                </div>
              )}
            </div>

            {/* Email Configuration */}
            <div className="p-4 bg-kova-bg border border-kova-cyan/12 space-y-3" data-testid="email-config">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Email Notifications</Label>
                  <p className="text-[10px] text-kova-slate mt-0.5">HTML alerts with severity styling via Resend</p>
                </div>
                <Switch
                  checked={notifSettings?.email_enabled || false}
                  onCheckedChange={(checked) => updateNotifSettings({ email_enabled: checked })}
                  data-testid="email-enabled-toggle"
                />
              </div>
              {notifSettings?.email_enabled && (
                <div className="space-y-2 pt-2 border-t border-kova-cyan/12">
                  <Label className="text-xs text-kova-slate">Recipients</Label>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      placeholder="alert@company.com"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmailRecipient(); } }}
                      className="flex-1 bg-kova-card border border-kova-cyan/12 text-white font-mono text-xs p-2 outline-none focus:border-kova-cyan/50"
                      data-testid="email-recipient-input"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addEmailRecipient}
                      className="border-kova-cyan/12 hover:bg-kova-card2 rounded-none text-xs font-mono"
                      data-testid="add-email-btn"
                    >
                      Add
                    </Button>
                  </div>
                  {notifSettings?.email_recipients?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {notifSettings.email_recipients.map(email => (
                        <span key={email} className="inline-flex items-center gap-1.5 bg-kova-card border border-kova-cyan/12 px-2 py-1 text-[11px] font-mono text-kova-silv">
                          {email}
                          <button onClick={() => removeEmailRecipient(email)} className="text-kova-slate hover:text-kova-red">&times;</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Severity Routing */}
            <div className="p-4 bg-kova-bg border border-kova-cyan/12 space-y-3" data-testid="severity-routing">
              <Label className="text-sm font-medium">Severity Routing</Label>
              <p className="text-[10px] text-kova-slate">Choose which alert severities trigger push notifications</p>
              <div className="space-y-2 pt-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-kova-red" />
                    <span className="text-xs">Critical — retries exhausted, auth failures</span>
                  </div>
                  <Switch
                    checked={notifSettings?.notify_on_critical ?? true}
                    onCheckedChange={(checked) => updateNotifSettings({ notify_on_critical: checked })}
                    data-testid="severity-critical-toggle"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-kova-gold" />
                    <span className="text-xs">Warning — response threshold, partial failures</span>
                  </div>
                  <Switch
                    checked={notifSettings?.notify_on_warning ?? true}
                    onCheckedChange={(checked) => updateNotifSettings({ notify_on_warning: checked })}
                    data-testid="severity-warning-toggle"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-kova-cyan" />
                    <span className="text-xs">Info — schema changes, successful recoveries</span>
                  </div>
                  <Switch
                    checked={notifSettings?.notify_on_info ?? false}
                    onCheckedChange={(checked) => updateNotifSettings({ notify_on_info: checked })}
                    data-testid="severity-info-toggle"
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="card-brutal p-6 space-y-4">
        <h2 className="font-heading font-bold text-lg flex items-center gap-2"><Bell size={20} className="text-kova-cyan" />Usage Statistics</h2>
        {stats ? (
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-kova-bg border border-kova-cyan/12"><div className="text-2xl font-bold font-heading">{stats.connections?.total || 0}</div><div className="text-xs text-kova-slate font-mono mt-1">TOTAL CONNECTIONS</div></div>
            <div className="p-4 bg-kova-bg border border-kova-cyan/12"><div className="text-2xl font-bold font-heading">{stats.jobs?.total || 0}</div><div className="text-xs text-kova-slate font-mono mt-1">SCHEDULED JOBS</div></div>
            <div className="p-4 bg-kova-bg border border-kova-cyan/12"><div className="text-2xl font-bold font-heading">{stats.executions_24h?.total || 0}</div><div className="text-xs text-kova-slate font-mono mt-1">EXECUTIONS (24H)</div></div>
            <div className="p-4 bg-kova-bg border border-kova-cyan/12"><div className="text-2xl font-bold font-heading text-kova-red">{stats.executions_24h?.failed || 0}</div><div className="text-xs text-kova-slate font-mono mt-1">FAILURES (24H)</div></div>
          </div>
        ) : <p className="text-kova-slate">Loading statistics...</p>}
      </div>

      {/* ═══ Rate Limiting / Quotas ═══ */}
      <div className="card-brutal p-6 space-y-4" data-testid="quota-settings">
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-bold text-lg flex items-center gap-2"><Gauge size={20} className="text-kova-cyan" />Rate Limits &amp; Quotas</h2>
          <button onClick={() => setEditingQuotas(!editingQuotas)} className="text-[10px] font-mono text-kova-slate hover:text-kova-cyan transition-colors" data-testid="edit-quotas-btn">
            {editingQuotas ? 'Cancel' : 'Edit'}
          </button>
        </div>
        {quotas ? (
          editingQuotas ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[10px] font-mono text-kova-slate uppercase">Hourly Limit</Label>
                  <input type="number" value={quotaForm.hourly_limit} onChange={e => setQuotaForm(f => ({ ...f, hourly_limit: parseInt(e.target.value) || 10 }))} className="mt-1 w-full bg-kova-bg border border-kova-cyan/12 text-sm text-white px-3 py-2 focus:outline-none focus:border-kova-cyan/50 font-mono" data-testid="hourly-limit-input" />
                </div>
                <div>
                  <Label className="text-[10px] font-mono text-kova-slate uppercase">Daily Limit</Label>
                  <input type="number" value={quotaForm.daily_limit} onChange={e => setQuotaForm(f => ({ ...f, daily_limit: parseInt(e.target.value) || 50 }))} className="mt-1 w-full bg-kova-bg border border-kova-cyan/12 text-sm text-white px-3 py-2 focus:outline-none focus:border-kova-cyan/50 font-mono" data-testid="daily-limit-input" />
                </div>
              </div>
              <Button onClick={() => {
                fetch(`${API}/settings/quotas`, {
                  method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
                  body: JSON.stringify(quotaForm),
                }).then(r => {
                  if (r.ok) { setQuotas(q => ({ ...q, ...quotaForm })); setEditingQuotas(false); toast.success('Quotas updated'); }
                  else toast.error('Failed to update');
                });
              }} size="sm" className="bg-kova-cyan text-kova-bg hover:bg-kova-cyan/85 rounded-none font-mono text-[9px] uppercase" data-testid="save-quotas-btn">Save Quotas</Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-kova-bg border border-kova-cyan/12">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-mono text-kova-slate uppercase">Hourly</span>
                  <span className="text-[10px] font-mono text-kova-silv">{quotas.hourly_usage}/{quotas.hourly_limit}</span>
                </div>
                <div className="h-1.5 bg-kova-cyan/12 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{
                    width: `${Math.min(100, (quotas.hourly_usage / quotas.hourly_limit) * 100)}%`,
                    background: quotas.hourly_remaining < quotas.hourly_limit * 0.1 ? '#E24B4A' : '#0ABFA3',
                  }} />
                </div>
                <div className="text-[10px] font-mono text-kova-slate mt-1">{quotas.hourly_remaining} remaining</div>
              </div>
              <div className="p-4 bg-kova-bg border border-kova-cyan/12">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-mono text-kova-slate uppercase">Daily</span>
                  <span className="text-[10px] font-mono text-kova-silv">{quotas.daily_usage}/{quotas.daily_limit}</span>
                </div>
                <div className="h-1.5 bg-kova-cyan/12 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{
                    width: `${Math.min(100, (quotas.daily_usage / quotas.daily_limit) * 100)}%`,
                    background: quotas.daily_remaining < quotas.daily_limit * 0.1 ? '#E24B4A' : '#0ABFA3',
                  }} />
                </div>
                <div className="text-[10px] font-mono text-kova-slate mt-1">{quotas.daily_remaining} remaining</div>
              </div>
            </div>
          )
        ) : <p className="text-kova-slate text-sm">Loading quotas...</p>}
      </div>

      {/* ═══ Workspace / Team Support ═══ */}
      <div className="card-brutal p-6 space-y-4" data-testid="workspace-settings">
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-bold text-lg flex items-center gap-2"><UsersThree size={20} className="text-kova-cyan" />Workspaces</h2>
          <Button onClick={() => setShowCreateWs(!showCreateWs)} size="sm" variant="outline" className="rounded-none border-kova-cyan/12 font-mono text-[9px] uppercase h-7 px-3" data-testid="create-workspace-btn">
            <Plus size={10} className="mr-1" />New Workspace
          </Button>
        </div>
        {showCreateWs && (
          <div className="flex gap-2" data-testid="create-ws-form">
            <input value={wsName} onChange={e => setWsName(e.target.value)} placeholder="Workspace name" className="flex-1 bg-kova-bg border border-kova-cyan/12 text-sm text-white px-3 py-2 focus:outline-none focus:border-kova-cyan/50 font-mono" data-testid="ws-name-input" />
            <Button onClick={() => {
              if (!wsName.trim()) return;
              fetch(`${API}/workspaces`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ name: wsName }) })
                .then(r => r.ok ? r.json() : null)
                .then(ws => { if (ws) { setWorkspaces(prev => [ws, ...prev]); setWsName(''); setShowCreateWs(false); toast.success('Workspace created'); } })
                .catch(() => toast.error('Failed'));
            }} size="sm" className="bg-kova-cyan text-kova-bg hover:bg-kova-cyan/85 rounded-none font-mono text-[9px] uppercase" data-testid="save-ws-btn">Create</Button>
          </div>
        )}
        {/* Pending Invites */}
        {pendingInvites.length > 0 && (
          <div className="space-y-2" data-testid="pending-invites">
            <h3 className="text-[10px] font-mono text-kova-gold uppercase tracking-wider">Pending Invitations</h3>
            {pendingInvites.map(inv => (
              <div key={inv.invite_id} className="flex items-center justify-between p-3 bg-kova-bg border border-kova-gold/20" data-testid={'invite-' + inv.invite_id}>
                <div>
                  <span className="text-sm text-kova-silv">{inv.workspace_name}</span>
                  <span className="text-[9px] font-mono text-kova-slate ml-2">as {inv.role}</span>
                  <span className="text-[9px] font-mono text-kova-slate ml-2">from {inv.invited_by_name}</span>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => {
                    fetch(`${API}/invites/${inv.invite_id}/accept`, { method: 'POST', credentials: 'include' })
                      .then(r => r.ok ? r.json() : null)
                      .then(d => {
                        if (d) {
                          setPendingInvites(p => p.filter(i => i.invite_id !== inv.invite_id));
                          fetch(`${API}/workspaces`, { credentials: 'include' }).then(r => r.ok ? r.json() : []).then(setWorkspaces);
                          toast.success('Joined workspace');
                        }
                      });
                  }} size="sm" className="bg-kova-cyan/15 text-kova-cyan border border-kova-cyan/30 hover:bg-kova-cyan/25 rounded-none font-mono text-[9px] uppercase h-6 px-2" data-testid={'accept-invite-' + inv.invite_id}>Accept</Button>
                  <Button onClick={() => {
                    fetch(`${API}/invites/${inv.invite_id}/decline`, { method: 'POST', credentials: 'include' })
                      .then(r => { if (r.ok) { setPendingInvites(p => p.filter(i => i.invite_id !== inv.invite_id)); toast.success('Declined'); } });
                  }} size="sm" variant="outline" className="rounded-none border-kova-cyan/12 font-mono text-[9px] uppercase h-6 px-2">Decline</Button>
                </div>
              </div>
            ))}
          </div>
        )}
        {/* Workspaces List */}
        <div className="space-y-3">
          {workspaces.length === 0 ? (
            <p className="text-sm text-kova-slate font-mono py-4 text-center">No workspaces yet. Create one to collaborate with your team.</p>
          ) : workspaces.map(ws => (
            <div key={ws.workspace_id} className="p-4 bg-kova-bg border border-kova-cyan/12" data-testid={'workspace-' + ws.workspace_id}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="font-heading font-bold text-sm">{ws.name}</span>
                  {ws.owner_id === user?.user_id && <Crown size={12} className="text-kova-gold" />}
                </div>
                <span className="text-[9px] font-mono text-kova-slate">{ws.members?.length || 0} member{(ws.members?.length || 0) !== 1 ? 's' : ''}</span>
              </div>
              <div className="space-y-1.5 mb-3">
                {ws.members?.map(m => (
                  <div key={m.user_id} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-kova-green" />
                      <span className="text-kova-silv">{m.name || m.email}</span>
                    </div>
                    <span className="text-[9px] font-mono text-kova-slate uppercase">{m.role}</span>
                  </div>
                ))}
              </div>
              {ws.owner_id === user?.user_id && (
                <div className="flex gap-2 mt-2">
                  {invitingWs === ws.workspace_id ? (
                    <div className="flex-1 flex gap-2">
                      <input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="Email address" className="flex-1 bg-kova-card border border-kova-cyan/12 text-xs font-mono text-white px-2 py-1 focus:outline-none focus:border-kova-cyan/50" data-testid="invite-email-input" />
                      <select value={inviteRole} onChange={e => setInviteRole(e.target.value)} className="bg-kova-card border border-kova-cyan/12 text-xs font-mono text-kova-silv px-2 py-1" data-testid="invite-role-select">
                        <option value="viewer">Viewer</option>
                        <option value="editor">Editor</option>
                        <option value="admin">Admin</option>
                      </select>
                      <button onClick={() => {
                        if (!inviteEmail.trim()) return;
                        fetch(`${API}/workspaces/${ws.workspace_id}/invite`, {
                          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
                          body: JSON.stringify({ email: inviteEmail, role: inviteRole }),
                        }).then(r => {
                          if (r.ok) { toast.success(`Invited ${inviteEmail}`); setInviteEmail(''); setInvitingWs(null); }
                          else r.json().then(d => toast.error(d.detail || 'Failed'));
                        });
                      }} className="text-kova-cyan hover:bg-kova-cyan/10 px-2 transition-colors" data-testid="send-invite-btn"><PaperPlaneTilt size={14} /></button>
                      <button onClick={() => setInvitingWs(null)} className="text-kova-slate hover:text-white px-1 transition-colors"><X size={14} /></button>
                    </div>
                  ) : (
                    <>
                      <Button onClick={() => setInvitingWs(ws.workspace_id)} size="sm" variant="outline" className="rounded-none border-kova-cyan/12 font-mono text-[9px] uppercase h-6 px-2" data-testid={'invite-to-' + ws.workspace_id}>
                        <PaperPlaneTilt size={10} className="mr-1" />Invite
                      </Button>
                      <Button onClick={() => {
                        if (!confirm('Delete workspace "' + ws.name + '"?')) return;
                        fetch(`${API}/workspaces/${ws.workspace_id}`, { method: 'DELETE', credentials: 'include' })
                          .then(r => { if (r.ok) { setWorkspaces(prev => prev.filter(w => w.workspace_id !== ws.workspace_id)); toast.success('Deleted'); } });
                      }} size="sm" variant="outline" className="rounded-none border-kova-red/30 text-kova-red font-mono text-[9px] uppercase h-6 px-2" data-testid={'delete-ws-' + ws.workspace_id}>
                        <Trash size={10} className="mr-1" />Delete
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="card-brutal p-6 space-y-4 border-kova-red/30">
        <h2 className="font-heading font-bold text-lg flex items-center gap-2 text-kova-red"><Trash size={20} />Danger Zone</h2>
        <p className="text-kova-slate text-sm">Sign out of your account. Your data will be preserved.</p>
        <Button data-testid="settings-logout-button" onClick={handleLogout} variant="outline" className="border-kova-red/50 text-kova-red hover:bg-kova-red/10 rounded-none">Sign Out</Button>
      </div>
    </div>
  );
}
